<template>
  <li
    class="sb-item"
    :class="{
      'sb-item--open': isOpen,
      'sb-item--active': isActiveSelf || hasActiveChild,
      'sb-item--separator': item.separator,
    }"
    :style="separatorStyle"
  >
    <!-- ---------- Parent (has children) ---------- -->
    <template v-if="hasChildren">
      <button
        type="button"
        class="sb-link sb-link--parent"
        :class="{ 'sb-link--active': hasActiveChild }"
        :style="indentStyle"
        :aria-expanded="String(railFlyoutMode ? flyoutOpen : isOpen)"
        :title="collapsed ? item.title : null"
        @click="onParentClick"
        @mouseenter="onEnter"
        @mouseleave="onLeave"
        @focus="onEnter"
        @blur="onLeave"
      >
        <i
          v-if="item.icon"
          class="sb-icon"
          :class="item.icon"
          aria-hidden="true"
        />
        <span class="sb-title">{{ item.title }}</span>
        <i
          class="sb-arrow fa fa-angle-right"
          :class="{ 'sb-arrow--open': isOpen }"
          aria-hidden="true"
        />
      </button>

      <!-- Inline accordion submenu (expanded state) -->
      <transition
        name="sb-expand"
        @enter="onExpandEnter"
        @after-enter="resetExpandHeight"
        @leave="onExpandLeave"
        @after-leave="resetExpandHeight"
      >
        <ul
          v-if="!railFlyoutMode && isOpen"
          class="sb-sublist"
        >
          <sidebar-item
            v-for="(child, i) in item.child"
            :key="i"
            :item="child"
            :collapsed="false"
            :depth="depth + 1"
            @item-click="$emit('item-click', $event)"
          />
        </ul>
      </transition>

      <!-- Collapsed rail flyout: hovering the icon shows just this group's children -->
      <transition
        v-if="railFlyoutMode"
        name="sb-fly"
      >
        <div
          v-show="flyoutOpen"
          class="sb-flyout"
          :style="flyoutStyle"
          @mouseenter="onEnter"
          @mouseleave="onLeave"
        >
          <div class="sb-flyout__header">
            {{ item.title }}
          </div>
          <ul class="sb-sublist sb-sublist--flyout">
            <sidebar-item
              v-for="(child, i) in item.child"
              :key="i"
              :item="child"
              :collapsed="false"
              :depth="1"
              @item-click="onFlyoutChildClick"
            />
          </ul>
        </div>
      </transition>
    </template>

    <!-- ---------- Leaf ---------- -->
    <template v-else>
      <router-link
        v-if="isLink"
        :to="item.href"
        class="sb-link"
        :class="{ 'sb-link--active': isActiveSelf }"
        :style="indentStyle"
        :title="collapsed ? item.title : null"
        @click.native="onLeafClick"
      >
        <i
          v-if="item.icon"
          class="sb-icon"
          :class="item.icon"
          aria-hidden="true"
        />
        <span class="sb-title">{{ item.title }}</span>
      </router-link>
      <a
        v-else
        href="#"
        class="sb-link"
        :style="indentStyle"
        :title="collapsed ? item.title : null"
        @click.prevent="onLeafClick"
      >
        <i
          v-if="item.icon"
          class="sb-icon"
          :class="item.icon"
          aria-hidden="true"
        />
        <span class="sb-title">{{ item.title }}</span>
      </a>
    </template>
  </li>
</template>

<script>
// Resolve an item's href (string or { path }) to a plain path string, or null.
function hrefToPath(href) {
  if (!href) {return null;}
  return typeof href === 'string' ? href : href.path || null;
}

// Does this single item's href match the current route path?
function itemMatchesRoute(item, routePath) {
  const path = hrefToPath(item.href);
  if (!path) {return false;}
  if (item.exactPath) {return routePath === path;}
  return routePath === path || routePath.startsWith(`${path}/`);
}

// Does this item or any descendant match the current route?
function branchMatchesRoute(item, routePath) {
  if (itemMatchesRoute(item, routePath)) {return true;}
  if (item.child && item.child.length) {
    return item.child.some((child) => branchMatchesRoute(child, routePath));
  }
  return false;
}

export default {
  name: 'SidebarItem',
  props: {
    item: {
      type: Object,
      required: true,
    },
    collapsed: {
      type: Boolean,
      default: false,
      description: 'Whether this item renders in the collapsed icon rail',
    },
    depth: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      open: false,
      flyoutOpen: false,
      flyoutStyle: {},
      leaveTimer: null,
    };
  },
  computed: {
    hasChildren() {
      return Array.isArray(this.item.child) && this.item.child.length > 0;
    },
    // Top-level parents in the collapsed rail use a hover flyout instead of the
    // inline accordion.
    railFlyoutMode() {
      return this.collapsed && this.hasChildren;
    },
    isLink() {
      return !!this.item.href;
    },
    isActiveSelf() {
      return itemMatchesRoute(this.item, this.$route.path);
    },
    hasActiveChild() {
      if (!this.hasChildren) {return false;}
      return this.item.child.some((child) => branchMatchesRoute(child, this.$route.path));
    },
    isOpen() {
      return this.open;
    },
    indentStyle() {
      if (this.collapsed) {return {};}
      return { paddingLeft: `${0.75 + (this.depth - 1) * 0.85}rem` };
    },
    // Align a separator line's left edge with where the icon/text starts:
    // the link's side margin (0.5rem) plus its padding-left (the indent).
    separatorStyle() {
      if (!this.item.separator) {return {};}
      const padLeft = this.collapsed ? 0.75 : 0.75 + (this.depth - 1) * 0.85;
      return { '--sb-separator-left': `${0.5 + padLeft}rem` };
    },
  },
  watch: {
    // Re-open the ancestor chain when navigating into a nested route.
    $route() {
      if (this.hasActiveChild) {this.open = true;}
    },
  },
  created() {
    // Auto-expand groups that contain the active route on first render.
    this.open = this.hasActiveChild;
  },
  beforeDestroy() {
    if (this.leaveTimer) {clearTimeout(this.leaveTimer);}
  },
  methods: {
    onParentClick(event) {
      // In the rail, the flyout is hover-driven; a click still toggles it so
      // keyboard/touch users can reach it.
      if (this.railFlyoutMode) {
        this.positionFlyout(event.currentTarget);
        this.flyoutOpen = !this.flyoutOpen;
        return;
      }
      this.open = !this.open;
    },
    onEnter(event) {
      if (this.leaveTimer) {
        clearTimeout(this.leaveTimer);
        this.leaveTimer = null;
      }
      if (this.railFlyoutMode) {
        const anchor = event.currentTarget.closest('.sb-item') || event.currentTarget;
        const button = anchor.querySelector('.sb-link--parent') || event.currentTarget;
        this.positionFlyout(button);
        this.flyoutOpen = true;
      }
    },
    onLeave() {
      if (!this.railFlyoutMode) {return;}
      this.leaveTimer = setTimeout(() => {
        this.flyoutOpen = false;
      }, 120);
    },
    // Anchor the fixed-position flyout beside the icon; flip upward near the
    // bottom of the viewport (the pinned user menu lives there).
    positionFlyout(buttonEl) {
      if (!buttonEl || typeof buttonEl.getBoundingClientRect !== 'function') {return;}
      const rect = buttonEl.getBoundingClientRect();
      const style = { left: `${rect.right + 2}px` };
      if (rect.top > window.innerHeight / 2) {
        style.bottom = `${window.innerHeight - rect.bottom}px`;
      } else {
        style.top = `${rect.top}px`;
      }
      this.flyoutStyle = style;
    },
    onLeafClick() {
      this.$emit('item-click', this.item);
    },
    onFlyoutChildClick(item) {
      this.flyoutOpen = false;
      this.$emit('item-click', item);
    },
    // Accordion height animation (JS hooks so we can animate to/from auto height).
    onExpandEnter(el) {
      el.style.height = '0';
      el.style.overflow = 'hidden';
      el.getBoundingClientRect(); // force reflow before animating
      el.style.height = `${el.scrollHeight}px`;
    },
    onExpandLeave(el) {
      el.style.height = `${el.scrollHeight}px`;
      el.style.overflow = 'hidden';
      el.getBoundingClientRect(); // force reflow before animating
      el.style.height = '0';
    },
    resetExpandHeight(el) {
      el.style.height = '';
      el.style.overflow = '';
    },
  },
};
</script>
